import React, { useContext, useEffect, useState } from 'react';
import { ActivityIndicator, Dimensions, ScrollView, StyleSheet, Text, View } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { GlobalStateContext } from '../components/general/GlobalStateProvider';
import { toHtmlTextStyle } from '../components/general/toHtmlTextStyle';
import AnnouncementDAO from '../data/AnnouncementDAO';
import { AnnouncementRecord } from '../interface/Announcement';

const WIDTH = Dimensions.get('window').width;
const PAGE_HORIZONTAL_PADDING = WIDTH >= 1200 ? 100 : 24;

const AnnouncementsScreen = () => {
    const { user } = useContext(GlobalStateContext);
    const [visibleAnnouncements, setVisibleAnnouncements] = useState<AnnouncementRecord[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadAnnouncements = async () => {
            setIsLoading(true);
            try {
                const records = await AnnouncementDAO.getAnnouncementsForUser(user);
                setVisibleAnnouncements(records);
            } catch (error) {
                console.error('Error loading announcements:', error);
                setVisibleAnnouncements([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadAnnouncements();
    }, [user]);

    return (
        <ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
            <View style={styles.container}>
                <View style={styles.hero}>
                    <View style={styles.heroBadge}>
                        <MaterialCommunityIcons name="bullhorn-outline" size={16} color="#1D4ED8" />
                        <Text style={styles.heroBadgeText}>Announcements</Text>
                    </View>

                    <h1 style={toHtmlTextStyle(styles.heroTitle, { marginTop: 0 })}>Latest updates from SnapCamp.</h1>
                    <Text style={styles.heroSubtitle}>
                        Important milestones, launch news, and major updates for families, camp owners, and partners.
                    </Text>
                    <Text style={styles.heroSupportText}>
                        {AnnouncementDAO.getAudienceSummary(user)}
                    </Text>
                </View>

                <View style={styles.timeline}>
                    {isLoading ? (
                        <View style={styles.emptyCard}>
                            <ActivityIndicator size="small" color="#1D4ED8" />
                            <Text style={styles.emptyTitle}>Loading announcements...</Text>
                        </View>
                    ) : visibleAnnouncements.length === 0 ? (
                        <View style={styles.emptyCard}>
                            <MaterialCommunityIcons name="map-marker-radius-outline" size={28} color="#1D4ED8" />
                            <Text style={styles.emptyTitle}>No announcements for this region yet.</Text>
                            <Text style={styles.emptyDescription}>
                                We will show local, state, national, and global updates here as they are published.
                            </Text>
                        </View>
                    ) : visibleAnnouncements.map((item) => (
                        <View key={item.announcementId || item.title} style={styles.announcementCard}>
                            <View style={styles.cardHeader}>
                                <View style={styles.announcementBadge}>
                                    <Text style={styles.announcementBadgeText}>{item.badge || 'Announcement'}</Text>
                                </View>
                                <View style={styles.scopeBadge}>
                                    <Text style={styles.scopeBadgeText}>{AnnouncementDAO.getAudienceLabel(item)}</Text>
                                </View>
                            </View>
                            <Text style={styles.announcementTitle}>{item.title}</Text>
                            <Text style={styles.announcementDescription}>{item.description}</Text>
                            {!!item.meta && <Text style={styles.announcementMeta}>{item.meta}</Text>}
                        </View>
                    ))}
                </View>
            </View>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: '#F8FAFC',
    },
    pageContent: {
        paddingTop: 36,
        paddingBottom: 72,
        paddingHorizontal: PAGE_HORIZONTAL_PADDING,
    },
    container: {
        width: '100%',
        gap: 28,
    },
    hero: {
        backgroundColor: '#FFFFFF',
        borderRadius: 28,
        padding: 32,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        shadowColor: '#0F172A',
        shadowOpacity: 0.06,
        shadowRadius: 18,
        shadowOffset: { width: 0, height: 8 },
    },
    heroBadge: {
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
        backgroundColor: '#DBEAFE',
        marginBottom: 18,
    },
    heroBadgeText: {
        fontSize: 13,
        fontWeight: '700',
        color: '#1D4ED8',
    },
    heroTitle: {
        fontSize: 36,
        lineHeight: 44,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 14,
    },
    heroSubtitle: {
        fontSize: 17,
        lineHeight: 28,
        color: '#475569',
    },
    heroSupportText: {
        fontSize: 14,
        lineHeight: 22,
        color: '#64748B',
        marginTop: 12,
    },
    timeline: {
        gap: 20,
    },
    announcementCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 24,
        padding: 28,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        shadowColor: '#0F172A',
        shadowOpacity: 0.05,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 6 },
    },
    cardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: 14,
        flexWrap: 'wrap',
    },
    announcementBadge: {
        alignSelf: 'flex-start',
        backgroundColor: '#0F172A',
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 999,
    },
    announcementBadgeText: {
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: '700',
        letterSpacing: 0.3,
    },
    scopeBadge: {
        alignSelf: 'flex-start',
        backgroundColor: '#DBEAFE',
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 999,
    },
    scopeBadgeText: {
        color: '#1D4ED8',
        fontSize: 12,
        fontWeight: '700',
    },
    announcementTitle: {
        fontSize: 28,
        lineHeight: 36,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 12,
    },
    announcementDescription: {
        fontSize: 16,
        lineHeight: 26,
        color: '#475569',
        marginBottom: 14,
    },
    announcementMeta: {
        fontSize: 14,
        lineHeight: 22,
        fontWeight: '700',
        color: '#1D4ED8',
    },
    emptyCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 24,
        padding: 32,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
    },
    emptyTitle: {
        fontSize: 20,
        lineHeight: 28,
        fontWeight: '700',
        color: '#0F172A',
        textAlign: 'center',
    },
    emptyDescription: {
        fontSize: 15,
        lineHeight: 24,
        color: '#64748B',
        textAlign: 'center',
        maxWidth: 540,
    },
});

export default AnnouncementsScreen;
